import {HttpErrorResponse, HttpHandlerFn, HttpRequest} from '@angular/common/http';
import {Auth, authState, IdTokenResult, User} from '@angular/fire/auth';
import {inject} from '@angular/core';
import {catchError, from, of, switchMap, take, throwError} from 'rxjs';
import {OrganizationService} from './services/organization.service';
import {SnackbarService} from './services/snackbar.service';
import {Router} from '@angular/router';
import {environment} from '../environments/environment';
import {expectedError} from './posthog-error-handler';

export function authInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn) {
  const auth = inject(Auth);
  const organizationService = inject(OrganizationService);
  const router = inject(Router);

  // Non-backend requests (e.g. viacep) — pass through without auth headers
  if (!req.url.startsWith(environment.backendUrl)) {
    return next(req);
  }

  // Public backend routes — pass through without auth headers
  const isPublicRoute = ['/auth/'].some(path => req.url.includes(path));
  if (isPublicRoute) {
    return next(req);
  }

  // All other backend requests require authentication
  return authState(auth).pipe(
    take(1),
    switchMap((user: User | null) => {
      if (!user) {
        router.navigate(['/login']);
        return throwError(() => expectedError('User not authenticated'));
      }
      return from(user.getIdTokenResult(false)).pipe(
        catchError(err => {
          console.error('Auth interceptor: token retrieval failed', err);
          router.navigate(['/login']);
          return throwError(() => new Error('Token retrieval failed'));
        })
      );
    }),
    switchMap((tokenResult: IdTokenResult) => {
      const expiresAt = new Date(tokenResult.expirationTime).getTime();
      const now = Date.now();
      const needsRefresh = expiresAt - now < 60_000; // <1 minute left
      if (needsRefresh) {
        console.log('refreshing token for user')
      }
      const token$ = needsRefresh
        ? from(auth.currentUser!.getIdToken(true)).pipe(
            catchError(err => {
              console.error('Auth interceptor: token refresh failed', err);
              router.navigate(['/login']);
              return throwError(() => new Error('Token refresh failed'));
            })
          )
        : of(tokenResult.token);

      return token$.pipe(
        switchMap(token => {
          const headers: {[key: string]: string} = {
            'selected-organization': req.headers.get('selected-organization') || organizationService.currentOrganizationId,
            Authorization: `Bearer ${token}`
          };
          // Add user impersonation header if active
          const impersonatingUserId = localStorage.getItem('impersonatingUserId');
          if (impersonatingUserId) {
            headers['impersonating-user-id'] = impersonatingUserId;
          }
          const authReq = req.clone({ setHeaders: headers });
          return next(authReq);
        })
      );
    })
  );
}

export function errorInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn) {
  const snackbar = inject(SnackbarService);
  const router = inject(Router);
  const organizationService = inject(OrganizationService);
  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 402 && error.error?.error === 'BILLING_INACTIVE') {
        if (error.error?.billingStatus) {
          organizationService.lastBillingStatus.set(error.error.billingStatus);
        }
        router.navigate(['/assinatura']);
        return throwError(() => error);
      }
      if (error.status >= 500) {
        const reason = error.error?.error || error.error?.message || error.statusText || 'Erro interno do servidor';
        snackbar.openSnackBar('error', `Erro ${error.status}: ${reason}`, 'Fechar', 8000);
      }
      return throwError(() => error);
    })
  );
}
